import React from 'react';
import styled from 'styled-components';
import Dropzone from 'react-dropzone';

import Modal from 'views/components/UI/DefaultModal';
import { Stack, PrimaryButton, DefaultButton, Text } from 'office-ui-fabric-react';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { theme } from 'theme';

import {
  importData as importDataRequest
} from 'api/handbooks/table';

import { IMessageType } from '../SingleTable/index';
import { apiTypes }     from 'shared/consts/handbooksApiTypes';

const FileName = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 15px 20px;
  padding: 0 20px;
  background-color: ${theme.colors.ultraLight};
`;

const DropDiv = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-width: 2px;
  border-radius: 2px;
  border-color: #eeeeee;
  border-style: dashed;
  background-color: #fafafa;
  color: #bdbdbd;
  outline: none;
  transition: border 0.24s ease-in-out;
  margin: 20px;
`;

const ErrorMessage = styled.span`
  color: #f00;
  display: flex;
  padding: 0 20px 20px;
`;

const Preloader = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background:rgba(255,255,255,0.5);
  z-index: 100;
`;

enum ErrorMessages {
  ReadFile = 'Ошибка чтения файла',
  EmptyFile = 'Загруженный файл пуст',
  InvalidFormat = 'Неверный формат файла'
}

interface IProps {
  handleClose: () => void;
  onSuccess: (errorData?: {errorsStr: string, errorMessage: [string], messageType: IMessageType}) => void;
  config: any;
}
interface IState {
  errorMsg: string;
  fileData: string | null;
  fileDataAsFile: File | null;
  fileName: string;
  isUploading: boolean;
}

class ImportDialog extends React.Component<IProps, IState> {
  state = {
    errorMsg: '',
    fileData: null,
    fileDataAsFile: null,
    fileName: '',
    isUploading: false
  };

  onDrop = async (acceptFiles: File[]) => {
    if (acceptFiles.length) {
      const fl: File = acceptFiles[0];
      const reader = new FileReader();
      reader.readAsText(fl);
      this.setState({ fileData: null, fileName: '' });
      reader.onload = () => {
        this.setState(
          {
            fileData: reader.result ? reader.result.toString() : null,
            fileName: fl.name
          },
          () => this.checkFileData()
        );
      };

      reader.onerror = () => {
        this.setState({ errorMsg: ErrorMessages.ReadFile });
      };
    }
  };

  downloadExample = () => {
    const { config } = this.props;
    const csv = `${config.import.header
      .map((el: { title: string }) => el.title)
      .join(';')}\r\n${config.import.example}`;
    const element = document.createElement('a');
    element.setAttribute(
      'href',
      'data:text/plain;charset=utf-8,' + encodeURIComponent(csv)
    );
    element.setAttribute('download', `${config.table.toLowerCase()}.txt`);

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  };

  checkFileData = async () => {
    const csv = this.state.fileData || '';
    const {header: confHeader, canWithoutCsvHeader} = this.props.config.import;

    if (csv.length === 0) {
      this.setState({ errorMsg: ErrorMessages.EmptyFile });
      return;
    };
    if (!canWithoutCsvHeader) {
      const headers = csv.split('\n')[0].split(';');
      if (headers.length !== confHeader.length) {
        this.setState({ errorMsg: ErrorMessages.InvalidFormat });
        return;
      };

      const mismatch = headers.find((value: string, index: number) => {
        if (value.trim() !== confHeader[index].title.trim()) return true;
      });
      if (mismatch) {
        this.setState({ errorMsg: ErrorMessages.InvalidFormat });
        return;
      };
    };
    this.setState({ errorMsg: '' });
  };

  uploadData = async () => {
    this.setState({ isUploading: true });
    const result = await importDataRequest(this.props.config, this.state.fileData);
    this.setState({ isUploading: false });
    if (result) {
      if (result.result === 'ok' || !result.error) {
        this.props.onSuccess(result);
      } else {
        this.props.onSuccess({
          errorsStr: result.error,
          errorMessage: result.error,
          messageType: 'error' as IMessageType,
        });
      }
    }
  }

  onCloseClick = () => {
    if (this.props.config.apiType !== apiTypes.DEFAULT) {
      this.setState({
                      errorMsg: '',
                      fileDataAsFile: null,
                      fileName: '',
                    });
    } else {
      this.setState({
                      errorMsg: '',
                      fileData: null,
                      fileName: '',
                    });
    }
  };

  render() {
    const { fileName, errorMsg } = this.state;
    return (
      <>
        <Modal
          title="Импортировать записи"
          handleClose={this.props.handleClose}
        >
          {this.state.isUploading && <Preloader /> }
          <Dropzone onDrop={this.onDrop} multiple={false}>
            {({ getRootProps, getInputProps }) => (
              <section className="container">
                <DropDiv {...getRootProps({ className: 'dropzone' })}>
                  <input {...getInputProps()} data-qa='inputFiles' />
                  <p>Переместите файл сюда, либо кликните и выберите файл.</p>
                </DropDiv>
                <aside>
                  {fileName &&
                    <FileName>
                      <p>{fileName}</p>
                      <Icon 
                        iconName='ChromeClose' 
                        onClick={this.onCloseClick}
                        styles={{
                          root: {
                            fontSize: '16px',
                            cursor: 'pointer',
                          }
                        }}
                      />
                    </FileName>
                  }
                  {
                    errorMsg !== '' &&
                    <ErrorMessage>{errorMsg}</ErrorMessage>
                  }
                </aside>
                <Stack
                  horizontal
                  horizontalAlign={'start'}
                  tokens={{ childrenGap: 10 }}
                  styles={{ root: { padding: '0 20px 20px' } }}
                >
                  <PrimaryButton text="Пример" onClick={this.downloadExample} />
                  <Stack.Item grow disableShrink>
                    <Stack
                      horizontal
                      horizontalAlign={'end'}
                      tokens={{ childrenGap: 10 }}
                    >
                      <PrimaryButton
                        text="Импортировать"
                        disabled={
                          this.state.fileData === null ||
                          this.state.errorMsg !== ''
                        }
                        onClick={this.uploadData}
                        data-qa={'importFilesBtn'}
                      />
                      <DefaultButton
                        text="Отмена"
                        onClick={this.props.handleClose}
                      />
                    </Stack>
                  </Stack.Item>
                </Stack>
              </section>
            )}
          </Dropzone>
        </Modal>
      </>
    );
  }
}

export default ImportDialog;
